import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Trash2, HelpCircle } from 'lucide-react';
import { Button } from './Button';
import { overlayStack } from '../../lib/overlayStack';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  isLoading = false
}: ConfirmDialogProps) {
  // Register with overlay stack for ESC and mobile back button
  const overlayIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      overlayIdRef.current = overlayStack.push(onClose);
    } else if (overlayIdRef.current) {
      overlayStack.remove(overlayIdRef.current);
      overlayIdRef.current = null;
    }
    return () => {
      if (overlayIdRef.current) {
        overlayStack.remove(overlayIdRef.current);
        overlayIdRef.current = null;
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (variant) {
      case 'danger': return <Trash2 className="w-6 h-6 text-rose-400" />;
      case 'warning': return <AlertTriangle className="w-6 h-6 text-amber-400" />;
      case 'info': return <HelpCircle className="w-6 h-6 text-sky-400" />;
    }
  };

  const getBadgeStyle = () => {
    switch (variant) {
      case 'danger': return 'bg-rose-500/10 border-rose-500/20';
      case 'warning': return 'bg-amber-500/10 border-amber-500/20';
      case 'info': return 'bg-sky-500/10 border-sky-500/20';
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-fade-in">
      <div className="bg-surface-secondary border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-5 animate-scale-in">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl border ${getBadgeStyle()} shrink-0`}>
            {getIcon()}
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-extrabold text-text-primary leading-snug">{title}</h3>
            <p className="text-xs text-text-secondary leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            variant={variant === 'danger' ? 'primary' : 'secondary'}
            size="sm"
            onClick={onConfirm}
            isLoading={isLoading}
            className={variant === 'danger' ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
